import React, { useState, useEffect, useRef, useCallback } from "react";
import "./css/Gallery.css";

const GALLERY_IMAGES = [
  { src: "/assets/more-eg-photos/fresh-landscape-backyard.jpg", alt: "Fresh landscape backyard", category: "landscape" },
  { src: "/assets/more-eg-photos/stone-path.jpg", alt: "Stone pathway", category: "hardscape" },
  { src: "/assets/eg-images/Final ex1.jpg", alt: "Garden transformation", category: "landscape" },
  { src: "/assets/more-eg-photos/front-door2.jpg", alt: "Front entrance garden", category: "entrance" },
  { src: "/assets/more-eg-photos/after-backyard.jpg", alt: "Backyard garden", category: "landscape" },
  { src: "/assets/more-eg-photos/pot-on-stone.jpg", alt: "Container garden", category: "planters" },
  { src: "/assets/more-eg-photos/curved-wall-plants.jpg", alt: "Curved wall planting", category: "landscape" },
  { src: "/assets/more-eg-photos/fresh-landscape-backyard2.jpg", alt: "Fresh landscape", category: "landscape" },
  { src: "/assets/more-eg-photos/stone-wall-plants.jpg", alt: "Stone wall garden", category: "hardscape" },
  { src: "/assets/more-eg-photos/indoor-planter.jpg", alt: "Indoor planter", category: "planters" },
  { src: "/assets/more-eg-photos/patio.jpg", alt: "Patio garden", category: "landscape" },
  { src: "/assets/more-eg-photos/float-pot.jpg", alt: "Floating planter", category: "planters" },
  { src: "/assets/more-eg-photos/skinny-tall-plant.jpg", alt: "Tall plant arrangement", category: "planters" },
  { src: "/assets/more-eg-photos/brick-path.jpg", alt: "Brick pathway", category: "hardscape" },
  { src: "/assets/more-eg-photos/front-door3.jpg", alt: "Front door garden", category: "entrance" },
];

export default function Gallery() {
  const [showModal, setShowModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const heroRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const refs = [heroRef, galleryRef];
    refs.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  const navigateImage = useCallback((direction) => {
    const newIndex = (currentIndex + direction + GALLERY_IMAGES.length) % GALLERY_IMAGES.length;
    setCurrentIndex(newIndex);
    setCurrentImage(GALLERY_IMAGES[newIndex]);
  }, [currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showModal) return;
      if (e.key === "Escape") setShowModal(false);
      if (e.key === "ArrowRight") navigateImage(1);
      if (e.key === "ArrowLeft") navigateImage(-1);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showModal, navigateImage]);

  const handleImageClick = (image, index) => {
    setCurrentImage(image);
    setCurrentIndex(index);
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentImage(null);
    document.body.style.overflow = "";
  };

  return (
    <div className="gallery-page">
      {/* Hero Section */}
      <section
        id="gallery-hero"
        ref={heroRef}
        className={`gallery-hero ${isVisible["gallery-hero"] ? "visible" : ""}`}
      >
        <div className="gallery-hero-background">
          <img
            src="/assets/eg-images/yard-bench-eg-og.jpg"
            alt="Gallery showcase"
          />
          <div className="gallery-hero-overlay" />
        </div>
        <div className="gallery-hero-content">
          <span className="section-label animate-fade-in-down">Portfolio</span>
          <h1 className="gallery-hero-title animate-fade-in-up delay-1">
            Our Gallery
          </h1>
          <p className="gallery-hero-subtitle animate-fade-in-up delay-2">
            Explore our collection of garden transformations and outdoor spaces.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section
        id="gallery-grid"
        ref={galleryRef}
        className={`gallery-grid-section ${isVisible["gallery-grid"] ? "visible" : ""}`}
      >
        <div className="gallery-grid-container">
          <div className="gallery-grid">
            {GALLERY_IMAGES.map((image, index) => (
              <div
                key={index}
                className={`gallery-item gallery-item-${(index % 6) + 1}`}
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => handleImageClick(image, index)}
              >
                <img src={image.src} alt={image.alt} />
                <div className="gallery-item-overlay">
                  <span className="gallery-item-icon">+</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {showModal && currentImage && (
        <div className="lightbox" onClick={closeModal}>
          <button className="lightbox-close" onClick={closeModal}>
            &times;
          </button>
          <button
            className="lightbox-nav lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage(-1);
            }}
          >
            &#8249;
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={currentImage.src} alt={currentImage.alt} />
            <p className="lightbox-caption">{currentImage.alt}</p>
          </div>
          <button
            className="lightbox-nav lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage(1);
            }}
          >
            &#8250;
          </button>
          <div className="lightbox-counter">
            {currentIndex + 1} / {GALLERY_IMAGES.length}
          </div>
        </div>
      )}
    </div>
  );
}
